/* eslint-disable @typescript-eslint/no-explicit-any,@typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access */
import { Component, forwardRef, HostBinding, Input, OnDestroy, OnInit } from '@angular/core'
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms'
import { merge, Observable, of, Subject } from 'rxjs'
import { debounceTime, filter, map, share, switchMap, takeUntil, tap } from 'rxjs/operators'
import { coerceBooleanProperty } from '@angular/cdk/coercion'

const autocompleteTriggerLength = 3

@Component({
  selector: 'lin-autocomplete-select',
  templateUrl: './autocomplete-select.component.html',
  styleUrls: ['./autocomplete-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteSelectComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class AutocompleteSelectComponent implements ControlValueAccessor, OnInit, OnDestroy {
  autocompleteOptions = []
  input: FormControl
  itemList = []
  isAutocompleteLoading = false
  selectedOption = null

  private _disabled: boolean

  @HostBinding('attr.disabled')
  @Input()
  set disabled(value) {
    this._disabled = coerceBooleanProperty(value)
  }
  get disabled() {
    return this._disabled
  }

  @Input() autocompleteHint: string
  @Input() autocompleteRequest: (value: string) => Observable<any>
  @Input() displayWith: (value: any) => string
  @Input() trackUniqueBy: (value: any) => string | number = JSON.stringify

  private readonly onDestroy$ = new Subject<void>()
  // eslint-disable-next-line @typescript-eslint/no-empty-function,@typescript-eslint/no-unused-vars
  private onChange = (value: any) => {}

  ngOnInit() {
    this.input = new FormControl()

    this.initInputHandler()
  }

  onOptionSelected(option: any) {
    this.selectedOption = option
  }

  addSelectedOptionToList() {
    this.itemList.push(this.selectedOption)

    this.selectedOption = null
    this.input.setValue('')

    this.onChange(this.itemList)

    const isNotSelected = (option) => option !== this.selectedOption
    this.autocompleteOptions = this.autocompleteOptions.filter(isNotSelected)
  }

  deleteItem(deletedItem) {
    const wasNotDeleted = (item) => item !== deletedItem
    this.itemList = this.itemList.filter(wasNotDeleted)
    this.onChange(this.itemList)
  }

  initInputHandler() {
    const valueChanges$ = this.input.valueChanges.pipe(
      takeUntil(this.onDestroy$),
      tap(() => {
        this.isAutocompleteLoading = true
        this.selectedOption = null
      }),
      share(),
    )

    const hideAutocomplete$ = valueChanges$.pipe(
      filter((value) => value.length < autocompleteTriggerLength),
      map(() => []),
    )

    const showAutocomplete$ = valueChanges$.pipe(
      switchMap((value) =>
        of(value).pipe(
          filter((v) => v.length >= autocompleteTriggerLength),
          debounceTime(300),
          switchMap(this.autocompleteRequest),
        ),
      ),
    )

    merge(hideAutocomplete$, showAutocomplete$)
      .pipe(tap(() => (this.isAutocompleteLoading = false)))
      .subscribe(this.setAutocompleteOptions)
  }

  setAutocompleteOptions = (options) => {
    const excludeThatAlreadyInList = (option) => {
      const trackedOption = this.trackUniqueBy(option)
      const matchOption = (item) => item === trackedOption
      return !this.itemList.map(this.trackUniqueBy).some(matchOption)
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.autocompleteOptions = options.filter(excludeThatAlreadyInList)
  }

  writeValue(itemList: any): void {
    if (itemList) {
      this.itemList = itemList
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  registerOnTouched(fn: any): void {
    // TODO not implemented
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setDisabledState(isDisabled: boolean): void {
    // TODO not implemented
  }

  ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }
}
